import React from 'react'
import { useCore, useLogin } from '../../../hooks'
import { useGroupFilters } from '../../../lib/hooks/useGroupFilters'
import { Button } from '../../../components/shadcn/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding, faSearch, faSort, faTag, faXmark } from '@fortawesome/pro-solid-svg-icons'
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/shadcn/popover'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '../../../components/shadcn/command'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'
import { Tag } from '../../../components/Tag'
import { useQueryClient } from '@tanstack/react-query'
import { ProfileDropdown } from '../../../components/ProfileDropdown'
import { useBasicEntities } from '../../../context/BasicEntitiesContext'
import { Combox } from '../../../components/shadcn/combox'

export function GroupsFilters(props: { onFilter?: () => void }) {
  const { onFilter } = props
  const { tags } = useBasicEntities()
  const { user } = useLogin()
  const queryClient = useQueryClient()
  const {
    selectedTagIds,
    setSelectedTagIds,
    resetFilters,
    selectedChildCompanyId,
    setSelectedChildCompany,
    sort,
    direction,
    setSort,
  } = useGroupFilters()

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['orderGroups'] })
    onFilter?.()
  }

  const filteredTags = tags.filter(tag => tag.type === 'order_group')
  const tagOptions = filteredTags.map(tag => ({ value: String(tag.id), label: tag.name }))
  const childCompanies = user.company.children || []
  const childCompanyOptions = [
    { value: 'all', label: 'Vše' },
    ...childCompanies.map(childCompany => ({ value: childCompany.id.toString(), label: childCompany.name })),
  ]

  return (
    <div className="orders-filters bg-white p-4 shadow mb-4">
      <div className={`grid grid-cols-4 gap-2`}>
        {childCompanies.length > 0 && (
          <div className="form-control gap-1">
            <label className="flex items-center gap-2">
              <FontAwesomeIcon icon={faBuilding} /> Firma
            </label>
            <Combox
              items={childCompanyOptions}
              placeholder="Vyberte firmu"
              value={selectedChildCompanyId ? String(selectedChildCompanyId) : 'all'}
              onValueChange={value => setSelectedChildCompany(value === 'all' ? undefined : value)}
            />
          </div>
        )}
        <div className="form-control gap-1">
          <label className="flex items-center gap-2">
            <FontAwesomeIcon icon={faTag} /> Tagy
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="justify-between text-left bg-gray-100" size="lg">
                <span className="truncate">
                  {selectedTagIds.length === 0
                    ? 'Vyberte tagy'
                    : selectedTagIds.length === 1
                      ? tagOptions.find(tag => tag.value === String(selectedTagIds[0]))?.label
                      : `${selectedTagIds.length} položek vybráno`}
                </span>
                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command>
                <CommandList>
                  <CommandEmpty>Nenalezeny žádné položky.</CommandEmpty>
                  <CommandGroup>
                    {tagOptions.map(tag => {
                      const tagId = Number(tag.value)
                      const isSelected = selectedTagIds.includes(tagId)
                      const tagData = filteredTags.find(t => t.id === tagId)
                      return (
                        <CommandItem
                          key={tag.value}
                          onSelect={() => {
                            setSelectedTagIds([tagId])
                          }}
                          className="cursor-pointer">
                          {tagData && <Tag tag={tagData} />}
                          <CheckIcon className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="form-control gap-1">
          <label className="flex items-center gap-2">
            <FontAwesomeIcon icon={faSort} /> Řazení podle
          </label>
          <Combox
            disableSearch
            items={[
              { value: 'ordersCount_desc', label: 'Počtu objednávek (Sestupně)' },
              { value: 'ordersCount_asc', label: 'Počtu objednávek (Vzestupně)' },
              { value: 'ordersDateExpeditionMIN_asc', label: 'Nejstaršího data expedice (Vzestupně) ' },
            ]}
            placeholder="Vyberte řazení"
            value={sort && direction ? `${sort}_${direction.toLowerCase()}` : undefined}
            onValueChange={value => {
              if (value) {
                const [field, dir] = value.split('_')
                setSort(field, dir.toUpperCase() as 'ASC' | 'DESC')
              }
            }}
          />
        </div>
        <div className="flex flex-1 items-end gap-2">
          <Button onClick={resetFilters} variant="outlineDanger" icon={faXmark} size="lg" />
          <Button onClick={refetch} variant="default" className="flex-1" size="lg">
            <FontAwesomeIcon icon={faSearch} />
            Vyhledat
          </Button>
        </div>
      </div>
    </div>
  )
}
